import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSummary, getKpis, getEvaluation, saveEvaluation, getCurrentCycle } from '../lib/db.js';
import { useAuth } from '../lib/auth.jsx';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';

export function EvaluationForm() {
  const { empId } = useParams();
  const nav = useNavigate();
  const { appUser } = useAuth();
  const [sum, setSum] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [scores, setScores] = useState({});
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [status, setStatus] = useState('not_started');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await getSummary(empId);
        if (!active) return;
        setSum(s);
        const [k, ev, c] = await Promise.all([getKpis(s?.position, s?.position_id), getEvaluation(empId), getCurrentCycle()]);
        if (!active) return;
        setKpis(k);
        setCycle(c);
        if (ev) {
          setScores(ev.scores || ev.kpi_summary || {});
          setStrengths(ev.strengths || '');
          setImprovements(ev.improvements || '');
          setStatus(ev.status || 'draft');
        }
      } catch (e) { if (active) setErr(e.message); }
    })();
    return () => { active = false; };
  }, [empId]);

  const overall = useMemo(() => {
    const tw = kpis.reduce((a, k) => a + k.weight, 0);
    const s = kpis.reduce((a, k) => a + (Number(scores[k.id]) || 0) * k.weight, 0);
    return tw ? Math.round(s / tw) : 0;
  }, [scores, kpis]);

  const allScored = kpis.length > 0 && kpis.every((k) => scores[k.id] !== undefined && scores[k.id] !== '');

  async function save(submit) {
    if (submit && !allScored) { setErr('ให้คะแนนครบทุก KPI ก่อนส่ง'); return; }
    setErr(''); setBusy(true);
    try {
      await saveEvaluation({
        cycle_id: cycle?.id, employee_id: empId, position: sum?.position, evaluator_id: appUser?.id,
        scores, overall_score: overall, strengths: strengths.trim(), improvements: improvements.trim(),
        status: submit ? 'submitted' : 'draft',
      });
      setStatus(submit ? 'submitted' : 'draft');
      setFlash(submit ? 'ส่งผลประเมินแล้ว — รอพนักงานรับทราบ' : 'บันทึกร่างแล้ว');
      setTimeout(() => setFlash(''), 2600);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const name = sum ? (sum.nickname || `${sum.first_name} ${sum.last_name}`) : '';

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        <div className="title-row">
          <div><div className="eyebrow">ประเมิน · {sum?.position || ''}</div><h1 className="page">{name}</h1></div>
          <span className="glyph">◷</span>
        </div>

        {err && <p className="err">{err}</p>}
        {flash && <div className="leave-note"><b>{flash}</b></div>}
        {!sum && !err && <div className="center"><div className="spinner" /></div>}

        {sum && (
          <>
            <div className="hero"><div className="num small">{overall}</div>
              <div className="sub">คะแนนรวม (ถ่วงน้ำหนัก) · {status === 'submitted' ? 'ส่งแล้ว' : status === 'acknowledged' ? 'รับทราบแล้ว' : 'ร่าง'}</div></div>

            <div className="sec-label">ข้อมูลประกอบ (อ้างอิง · อ่านอย่างเดียว)</div>
            <div className="grid">
              <Stat k="มาสาย (ปีนี้)" v={sum.late_count_ytd ?? 0} unit="ครั้ง" />
              <Stat k="ขาด (ปีนี้)" v={sum.absent_count_ytd ?? 0} unit="ครั้ง" />
              <Stat k="ใบเตือนมีผล" v={sum.active_warnings ?? 0} unit="ฉบับ" />
              <Stat k="ชมเชย" v={sum.commendation_count ?? 0} unit="ครั้ง" />
            </div>

            <div className="sec-label">KPI ตามตำแหน่ง (ให้คะแนน 0–100)</div>
            {kpis.map((k) => (
              <div key={k.id} style={{ marginTop: 14 }}>
                <label className="f" style={{ margin: '0 0 8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{k.name}</span><span style={{ color: 'var(--t3)' }}>น้ำหนัก {k.weight}%</span>
                </label>
                <input type="number" min="0" max="100" inputMode="numeric"
                  value={scores[k.id] ?? ''} placeholder="0–100"
                  onChange={(e) => setScores((p) => ({ ...p, [k.id]: e.target.value }))} />
              </div>
            ))}

            <label className="f">จุดเด่น</label>
            <textarea value={strengths} onChange={(e) => setStrengths(e.target.value)} placeholder="สิ่งที่ทำได้ดีในรอบนี้…" />
            <label className="f">สิ่งที่ควรพัฒนา</label>
            <textarea value={improvements} onChange={(e) => setImprovements(e.target.value)} placeholder="แนวทางพัฒนาในรอบถัดไป…" />

            <button className="cta" disabled={busy} onClick={() => save(true)}>
              {busy ? 'กำลังบันทึก…' : 'ส่งผลประเมิน · ให้พนักงานรับทราบ'}
            </button>
            <button className="cta ghost" style={{ marginTop: 10 }} disabled={busy} onClick={() => save(false)}>บันทึกร่าง</button>
            <p className="note" style={{ marginTop: 14, textAlign: 'center' }}>
              ทุกคนถูกวัดด้วย KPI เดียวกันตามตำแหน่ง · ประกาศเกณฑ์ก่อนเริ่มรอบ
            </p>
          </>
        )}
      </section>
      <BottomNav />
    </div>
  );
}

function Stat({ k, v, unit }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v">{v} <small>{unit}</small></div>
    </div>
  );
}
